// src/components/downloads/Brand.jsx
import React, { useState, useMemo } from "react";
import {
  DocumentIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  BsFiletypeExe,
  BsFillFileEarmarkPdfFill,
  BsFillFileEarmarkPptFill,
  BsFillFileEarmarkZipFill,
} from "react-icons/bs";
import { getBrandThemeClasses } from "../../utils/brandThemeClasses";

const getFileExtension = (downloadFile) => {
  const fileUrl = downloadFile?.download_file?.url || downloadFile?.url || "";
  const fileName = downloadFile?.download_file?.name || downloadFile?.name || "";
  const fileTitle = downloadFile?.download_title || downloadFile?.title || "";
  const target = `${fileUrl} ${fileName} ${fileTitle}`.toLowerCase();

  if (target.includes(".pptx")) return "pptx";
  if (target.includes(".ppt")) return "ppt";
  if (target.includes(".pdf")) return "pdf";
  if (target.includes(".zip")) return "zip";
  if (target.includes(".exe")) return "exe";
  return "";
};

export const PDFCard = ({ downloadFile, rowIndex, themeId = "default" }) => {
  if (!downloadFile) return null;

  const fileUrl = downloadFile.download_file?.url || downloadFile.url;
  const fileTitle = downloadFile.download_title || downloadFile.title || "Document";
  const fileExtension = getFileExtension(downloadFile);
  const isOddRow = rowIndex % 2 === 0;

  const themeClasses = getBrandThemeClasses(themeId);

  const fileTypeConfig =
    fileExtension === "zip"
      ? { Icon: BsFillFileEarmarkZipFill, iconClass: "text-amber-600" }
      : fileExtension === "exe"
        ? { Icon: BsFiletypeExe, iconClass: "text-sky-600" }
      : fileExtension === "ppt" || fileExtension === "pptx"
        ? { Icon: BsFillFileEarmarkPptFill, iconClass: "text-orange-600" }
        : fileExtension === "pdf"
          ? { Icon: BsFillFileEarmarkPdfFill, iconClass: "text-red-600" }
          : { Icon: DocumentIcon, iconClass: themeClasses.cardFallbackIcon };
  const { Icon, iconClass } = fileTypeConfig;

  const handleOpenPDF = () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      onClick={handleOpenPDF}
      className={`group w-full backdrop-blur-md rounded-xl shadow-lg p-4 md:p-6 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] flex items-center gap-4 text-left border ${
        isOddRow
          ? themeClasses.cardRowOdd
          : themeClasses.cardRowEven
      }`}
    >
      <Icon className={`h-8 w-8 flex-shrink-0 ${iconClass}`} />
      <h3
        className={`text-lg md:text-xl font-semibold text-gray-900 ${themeClasses.titleHover} transition-colors duration-200 line-clamp-2 flex-1`}
      >
        {fileTitle}
      </h3>
    </button>
  );
};

const Brand = ({ brand, index = 0 }) => {
  if (!brand) return null;

  const { name, title, subtitle, logo, download_files = [] } = brand;
  const [searchQuery, setSearchQuery] = useState("");

  // Filter download files based on search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return download_files;
    }
    const query = searchQuery.toLowerCase().trim();
    return download_files.filter((file) => {
      const fileTitle = (file.download_title || file.title || "").toLowerCase();
      return fileTitle.includes(query);
    });
  }, [download_files, searchQuery]);

  return (
    <div
      className="panel-elevated backdrop-blur-md rounded-2xl shadow-xl p-6 md:p-8 lg:p-10 transition-all duration-300 hover:shadow-2xl"
      style={{
        animationDelay: `${index * 150}ms`,
      }}
    >
      {/* Brand header: left text, right logo (aligned with products brand header) */}
      {(name || title || (logo && logo.url)) && (
        <div className="mb-6 md:mb-8 flex items-center justify-between gap-4">
          <div>
            {(name || title) && (
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {name || title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-1 text-sm sm:text-base text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
          {logo && logo.url && (
            <div className="ml-4 flex-shrink-0 flex items-center justify-center">
              <img
                src={logo.url}
                alt={logo.alternativeText || logo.name || "Brand Logo"}
                className="h-12 sm:h-14 md:h-16 w-auto object-contain"
              />
            </div>
          )}
        </div>
      )}

      {/* Search Bar */}
      {download_files && download_files.length > 0 && (
        <div className="mb-6 md:mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search files..."
              className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>
      )}

      {/* Download Files List */}
      {filteredFiles && filteredFiles.length > 0 ? (
        <div className="space-y-4 md:space-y-6">
          {filteredFiles.map((downloadFile, fileIndex) => (
            <PDFCard
              key={downloadFile.id || fileIndex}
              downloadFile={downloadFile}
              rowIndex={fileIndex}
            />
          ))}
        </div>
      ) : download_files && download_files.length > 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No files found matching "{searchQuery}".</p>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>No download files available for this brand.</p>
        </div>
      )}
    </div>
  );
};

export default Brand;

